import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion } from '@/components/ui/accordion';
import { Sparkles } from 'lucide-react';
import { pricingPlans, pricingFaqs } from '@/lib/pricingData';
import { PricingToggle } from '@/components/pricing/PricingToggle';
import { PricingCard } from '@/components/pricing/PricingCard';
import { FaqItem } from '@/components/faq/FaqItem';
import SEO from '@/components/seo/SEO';
import { motion } from 'framer-motion';

const PricingPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const appBaseUrl = import.meta.env.VITE_APP_URL || '';

  const pricingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: 'LendLifted Seller Financing Platform',
    description:
      'Software for small service businesses to offer financing-enabled checkout links, QR checkout, and buyer-intent conversion insights.',
    category: 'Seller Financing Software',
    feesAndCommissionsSpecification:
      'Platform pricing applies separately from payment processing and financing-provider fees at checkout.',
    url: `${import.meta.env.VITE_SITE_URL || 'https://lendlifted.com'}/pricing`,
  };

  return (
    <div className="min-h-screen bg-[#f8f5ef] font-sans text-slate-900 selection:bg-emerald-100 selection:text-emerald-900">
      <SEO
        title="LendLifted Pricing | Seller Financing Software Plans"
        description="Transparent LendLifted pricing for service sellers. Platform fees are separate from Stripe and financing-provider transaction fees."
        canonical="/pricing"
        jsonLd={pricingJsonLd}
      />

      <section className="relative overflow-hidden px-6 pb-20 pt-32 text-center">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-[-10rem] h-[28rem] w-[44rem] -translate-x-1/2 rounded-full bg-emerald-200/40 blur-3xl" />
        </div>
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <h1 className="mb-4 text-5xl font-extrabold tracking-tight text-slate-950 md:text-7xl">Only pay when you get paid.</h1>
            <p className="mx-auto mb-4 max-w-3xl rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
              LendLifted plan pricing covers platform access, quote flow, and sales insights. Stripe/payment-provider processing fees are separate.
            </p>
            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-slate-600">Pick the plan that matches your sales volume and close-rate goals.</p>
            <PricingToggle isAnnual={isAnnual} onToggle={setIsAnnual} />
          </motion.div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="container mx-auto">
          <div className="mx-auto grid max-w-5xl items-stretch gap-8 md:grid-cols-2">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 + 0.15, duration: 0.4 }}
              >
                <PricingCard plan={plan} isAnnual={isAnnual} appBaseUrl={appBaseUrl} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-50 py-24">
        <div className="container mx-auto max-w-3xl px-6">
          <div className="mb-14 text-center">
            <h2 className="mb-4 text-3xl font-bold text-slate-900 md:text-4xl">Common Questions</h2>
            <p className="text-slate-600">Everything you need to know about billing, usage, and rollout.</p>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-6">
            {pricingFaqs.map((faq, i) => (
              <FaqItem key={i} item={faq} index={i} />
            ))}
          </Accordion>
        </div>
      </section>

      <section className="px-6 py-24 text-center">
        <div className="mb-6 inline-flex items-center justify-center rounded-full bg-emerald-50 p-4 text-emerald-600">
          <Sparkles size={28} />
        </div>
        <h2 className="mb-5 text-3xl font-bold text-slate-900 md:text-4xl">Need help choosing a plan?</h2>
        <p className="mx-auto mb-8 max-w-xl text-lg text-slate-600">
          We&apos;ll recommend the right setup for your ticket size, volume, and financing strategy.
        </p>
        <Button variant="outline" className="h-12 rounded-xl border-slate-300 px-8 text-slate-800 hover:bg-slate-50" asChild>
          <Link to="/contact">Contact Sales</Link>
        </Button>
      </section>
    </div>
  );
};

export default PricingPage;
